import { landmineNames } from '@mm/constants/landmine-names';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { getDeviceInfo, getResolutionInfo } from '@mm/hooks';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { timeIntervalsPresets } from '../timePresets';
import './AnalyticsFilterByDate.scss';
import { CalendarOutlined, MinusOutlined } from '@ant-design/icons';

type Props = {
  startDate: Moment | null;
  setStartDate: Dispatch<SetStateAction<Moment | null>>;
  endDate: Moment | null;
  setEndDate: Dispatch<SetStateAction<Moment | null>>;
  setDateRangeMode: Dispatch<SetStateAction<string | null>>;
  dateRangeMode: string | null;
  presetsToExclude?: string[];
  showClearDates?: boolean;
  className?: string;
  instantChangeMode?: boolean;
};

export const AnalyticsFilterByDate = (props: Props) => {
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setDateRangeMode,
    dateRangeMode,
    presetsToExclude,
    className,
    showClearDates = true,
    instantChangeMode = false,
  } = props;
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  );
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localDateRangeMode, setLocalDateRangeMode] = useState(dateRangeMode);
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const { isMobile } = getDeviceInfo();
  const { screenWidth } = getResolutionInfo();
  const orientation =
    isMobile && screenWidth <= 600 ? 'vertical' : 'horizontal';

  const hasAccessToFilterByDateBotAnalytics = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsBotFilterByDate;

  const renderCalendarInfo = () => {
    const setTimeRange = (
      startTime: moment.Moment | null,
      endTime: moment.Moment | null,
      mode: string | null,
      local?: boolean,
    ) => {
      if (local) {
        setLocalStartDate(startTime);
        setLocalEndDate(endTime);
        setLocalDateRangeMode(mode);
      } else {
        setStartDate(startTime);
        setEndDate(endTime);
        setDateRangeMode(mode);
      }
    };

    const filteredTimeIntervalPresets = presetsToExclude
      ? timeIntervalsPresets.filter(
          preset => presetsToExclude.indexOf(preset.text) === -1,
        )
      : timeIntervalsPresets;

    const onClickPreset = (
      startTime: moment.Moment | null,
      endTime: moment.Moment | null,
      mode: string | null,
    ) => {
      setTimeRange(startTime, endTime, mode, true);
      setTimeRange(startTime, endTime, mode);
      setFocusedInput(null);
    };

    const onClickClear = () => {
      setTimeRange(null, null, 'Last 6 Months', true);
      setFocusedInput('startDate');
    };

    const onClickApply = () => {
      setTimeRange(localStartDate, localEndDate, localDateRangeMode);
      setFocusedInput(null);
    };

    const applyButtonDisabled =
      localStartDate === startDate && localEndDate === endDate;

    return (
      <div className="calendar__presets-ranges-container">
        {(!isMobile || screenWidth > 600) && (
          <div>
            {filteredTimeIntervalPresets.map(preset => {
              return (
                <div
                  key={preset.text}
                  className="calendar__presets-range"
                  onClick={() =>
                    onClickPreset(preset.startTime, preset.endTime, preset.text)
                  }
                >
                  {preset.text}
                </div>
              );
            })}
          </div>
        )}
        <div className="calendar__action-buttons">
          {showClearDates && (
            <div
              className={classNames(
                'calendar__presets-range',
                'calendar__presets-range-clear',
              )}
              onClick={onClickClear}
            >
              Clear
            </div>
          )}
          {!instantChangeMode && (
            <PrimaryButton
              disabled={applyButtonDisabled}
              className={classNames(
                'calendar__presets-range',
                'calendar__presets-range-primary',
              )}
              onClick={onClickApply}
            >
              Apply
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  };

  const onFocusChange = (focusedInput: FocusedInputShape) => {
    if (!hasAccessToFilterByDateBotAnalytics) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['BOT-ANALYTICS-FILTER-BY-DATE'],
        feature: 'analyticsBotFilterByDate',
      });
    } else {
      setFocusedInput(focusedInput);
      if (focusedInput === null) {
        setLocalStartDate(startDate);
        setLocalEndDate(endDate);
        setDateRangeMode(dateRangeMode);
        if (instantChangeMode) {
          setStartDate(startDate);
          setEndDate(endDate);
          setDateRangeMode(dateRangeMode);
        }
      }
    }
  };

  const classes = classNames('calendar', className);

  const initialVisibleMonth = () => moment().subtract(1, 'month');

  const onDatesChange = (dates: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => {
    const { startDate, endDate } = dates;
    if (instantChangeMode) {
      setStartDate(startDate);
      setEndDate(endDate);
      setDateRangeMode(null);
    }
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    setLocalDateRangeMode(null);
  };

  return (
    <div className={classes}>
      <DateRangePicker
        startDate={localStartDate}
        startDateId="start_date"
        endDate={localEndDate}
        endDateId="end_date"
        onDatesChange={onDatesChange}
        focusedInput={focusedInput}
        onFocusChange={onFocusChange}
        isOutsideRange={() => false}
        hideKeyboardShortcutsPanel
        showDefaultInputIcon
        keepOpenOnDateSelect
        renderCalendarInfo={renderCalendarInfo}
        calendarInfoPosition="before"
        initialVisibleMonth={initialVisibleMonth}
        orientation={orientation}
        customArrowIcon={<MinusOutlined />}
        customInputIcon={<CalendarOutlined />}
      />
    </div>
  );
};
